/** Wait before the first repeat toast — also the post-quiet reset value. */
export const INITIAL_TOAST_INTERVAL_MS = 3000;

/** Ceiling on the backoff: a persistent error still surfaces this often. */
export const MAX_TOAST_INTERVAL_MS = 60_000;

/** Quiet gap after which a new error is treated as unrelated and toasts at once. */
export const TOAST_BACKOFF_RESET_MS = 120_000;

export interface ToastBackoffState {
  /** When the last toast was actually shown (epoch ms); `null` = none yet. */
  lastShownAt: number | null;
  /** Required wait before the next toast, doubling per shown toast. */
  intervalMs: number;
}

export function initialToastBackoffState(): ToastBackoffState {
  return { lastShownAt: null, intervalMs: INITIAL_TOAST_INTERVAL_MS };
}

/**
 * Decide whether a global error should raise its (single, generic) toast, and
 * return the state to carry forward. Pure so the policy is unit-testable without
 * mounting the listener or mocking sonner.
 *
 * A flat throttle can't tell "one error" from "an error every second for three
 * minutes": at 3s it let a foreign-script storm fire 15 identical "An unexpected
 * error occurred." toasts in 175 seconds (replay c8a1b05f). The wait therefore
 * doubles after every toast shown, up to MAX_TOAST_INTERVAL_MS.
 *
 * It is capped rather than latched off, deliberately — a toast for a persistent
 * error that never comes back is its own bug (`performance-patterns.md` #9). The
 * backoff also measures from the last SHOWN toast, not the last error, so a
 * continuous storm can't keep resetting itself back to chatty.
 */
export function nextToastBackoff(
  state: ToastBackoffState,
  now: number
): { show: boolean; state: ToastBackoffState } {
  // First ever, or the trouble stopped long enough that this is a new event.
  // `null` rather than 0 for "never shown": an epoch of 0 is a legitimate
  // timestamp, and conflating them re-opens the storm on the very first repeat.
  const sinceLastShown =
    state.lastShownAt === null ? Infinity : now - state.lastShownAt;

  if (sinceLastShown >= TOAST_BACKOFF_RESET_MS) {
    return {
      show: true,
      state: { lastShownAt: now, intervalMs: INITIAL_TOAST_INTERVAL_MS },
    };
  }

  if (sinceLastShown < state.intervalMs) return { show: false, state };

  return {
    show: true,
    state: {
      lastShownAt: now,
      intervalMs: Math.min(state.intervalMs * 2, MAX_TOAST_INTERVAL_MS),
    },
  };
}
